"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import {
  getOrCreateMarketingJourneyIdentity,
  isContentMarketingPath,
  isPublicMarketingPath,
  parseMarketingAttributionFromLocation,
  storeMarketingAttribution,
} from "@/lib/marketing-attribution";

type MarketingEventPayload = {
  eventType: "page_view" | "internal_click";
  path: string;
  destinationPath?: string | null;
  linkLabel?: string | null;
  referrer?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  visitorId?: string | null;
  sessionId?: string | null;
};

function sendMarketingEvent(payload: MarketingEventPayload): void {
  void fetch("/api/marketing/hit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    /* Analytics must never block navigation. */
  });
}

/**
 * Records public page views, stores first-touch attribution for signup sync,
 * and records internal link clicks from guides, comparisons, and trade pages.
 */
export function MarketingHitTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const sentRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname || !isPublicMarketingPath(pathname)) return;
    const search = searchParams?.toString() ? `?${searchParams.toString()}` : "";
    const gate = `${pathname}${search}`;
    if (sentRef.current === gate) return;
    sentRef.current = gate;

    const identity = getOrCreateMarketingJourneyIdentity();
    const parsed = parseMarketingAttributionFromLocation(pathname, search, document.referrer ?? "");
    const attr = { ...parsed, ...identity };
    storeMarketingAttribution(attr);

    sendMarketingEvent({
      eventType: "page_view",
      path: pathname,
      referrer: attr.referrer,
      utmSource: attr.utmSource,
      utmMedium: attr.utmMedium,
      utmCampaign: attr.utmCampaign,
      visitorId: identity.visitorId,
      sessionId: identity.sessionId,
    });
  }, [pathname, searchParams]);

  useEffect(() => {
    if (!pathname || !isContentMarketingPath(pathname)) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest<HTMLAnchorElement>("a[href]");
      if (!anchor || anchor.hasAttribute("download")) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === pathname && (!url.hash || url.hash.startsWith("#"))) return;

      const identity = getOrCreateMarketingJourneyIdentity();
      sendMarketingEvent({
        eventType: "internal_click",
        path: pathname,
        destinationPath: `${url.pathname}${url.hash}`,
        linkLabel:
          anchor.getAttribute("aria-label")?.trim() ||
          anchor.textContent?.replace(/\s+/g, " ").trim().slice(0, 160) ||
          null,
        visitorId: identity.visitorId,
        sessionId: identity.sessionId,
      });
    };

    document.addEventListener("click", handleClick, { capture: true });
    return () => document.removeEventListener("click", handleClick, { capture: true });
  }, [pathname]);

  return null;
}